import { Check, Circle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/libs/cn'
import { PASSWORD_RULES } from '@/shared/constants/password'

interface PasswordRequirementsProps {
  id: string
  value: string
  /** Turn unmet rules red — set once the user has tried to submit a weak password. */
  highlightUnmet: boolean
}

/**
 * Live checklist of the password policy. Rules are listed up front (not only after an error) so
 * the user can see what to type, and each state carries an icon as well as a colour.
 */
export function PasswordRequirements({ id, value, highlightUnmet }: PasswordRequirementsProps) {
  const { t } = useTranslation()

  return (
    <div id={id} className="rounded-lg bg-zinc-50 px-3 py-2.5">
      <p className="text-xs font-medium text-zinc-600">{t('tenant.admin.passwordRules.title')}</p>
      <ul className="mt-1.5 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
        {PASSWORD_RULES.map((rule) => {
          const met = rule.isMet(value)
          return (
            <li
              key={rule.key}
              className={cn(
                'flex items-center gap-1.5 text-xs transition-colors',
                met ? 'text-emerald-700' : highlightUnmet ? 'text-red-600' : 'text-zinc-500',
              )}
            >
              {met ? (
                <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
              ) : (
                <Circle className="h-3.5 w-3.5 shrink-0" aria-hidden />
              )}
              <span>{t(`tenant.admin.passwordRules.${rule.key}`)}</span>
              <span className="sr-only">
                {met ? t('tenant.admin.passwordRules.met') : t('tenant.admin.passwordRules.unmet')}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
